import TextHelper from '@/core/helpers/TextHelper';

interface PaymentBenefitInstallments {
  requestBenefit: boolean;
  discountPayment: boolean;
}
interface IPaymentItem {
  status: string;
  name: string;
  amount: string;
  recharge?: string;
  dueDate: number;
  itemsIds?: string[];
  periodDesc?: string;
  itemNumber: string;
  amountWithBenefits?: string;
  amountToPay: string;
  hasDiscountProntoPago?: boolean;
  paymentType: {
    id: string;
    desc: string;
  };
  billName?: string;
  discountProntoPago?: number;
  discountCicloCompleto?: number;
  discountBene?: number;
  dateCreate?: number;
  billingDate?: number;
}

export const formatAmount = (Amount: string): string =>
  `${Number(Amount ?? 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

export const formatPaymentItems = (
  paymentsResults: any,
  benefitInstallments: PaymentBenefitInstallments
): IPaymentItem[] => {
  if (!paymentsResults) return [];
  const { items, summary } = paymentsResults;
  const { requestBenefit, discountPayment } = benefitInstallments;

  const formattedItems: IPaymentItem[] = items
    ?.filter((item: any) => {
      if (!requestBenefit || !discountPayment) return true;
      return item.paymentType.id !== '02';
    })
    .map((item: any) => ({
      status: TextHelper.capitalize(item.status),
      name: TextHelper.capitalize(item.name),
      itemNumber: item.itemNumber,
      dueDate: item.dueDate,
      amount: item.amount as number,
      recharge: item.recharge > 0 ? (item.recharge as number) : undefined,
      periodDesc: item.periodDesc,
      amountWithBenefits: item.amountWithBenefits,
      amountToPay: item.amountToPay,
      paymentType: item.paymentType,
      billName: item.billName,
      discountProntoPago: item.discountProntoPago,
      discountCicloCompleto: item.discountCicloCompleto,
      discountBene: item.discountBene,
      dateCreate: item.dateCreate
    }));

  if (discountPayment && requestBenefit) {
    const itemWithDiscount: IPaymentItem = {
      itemNumber: 'total',
      status: 'No Pagado',
      name: `Ciclo Completo`,
      amount: summary?.totalInstallmentsToPaid,
      amountToPay: summary?.totalInstallmentsToPaid,
      dueDate: 2122024,
      paymentType: {
        id: '10%',
        desc: 'Ciclo completo'
      },
      itemsIds: items
        ?.filter(
          (item: any) =>
            item.status.toUpperCase() !== 'PAGADA' &&
            item.paymentType.id === '02'
        )
        .map((item: any) => item.itemNumber)
    };
    if (itemWithDiscount.itemsIds?.length)
      formattedItems.push(itemWithDiscount);
  }

  return formattedItems.sort((a, b) => (a.dueDate ?? 0) - (b.dueDate ?? 0));
};

export function formatInstallments(pendingPayments: any) {
  return pendingPayments?.items?.filter(
    (item: any) =>
      item.paymentType.id === '02' && item.status.toUpperCase() !== 'VENCIDO'
  );
}

export const formatDiscountBenefitItems = (
  pendingPayments: any,
  benefitInstallments: PaymentBenefitInstallments
): any => {
  if (!pendingPayments || !benefitInstallments) return [];
  const { items } = pendingPayments;
  const { requestBenefit, discountPayment } = benefitInstallments;

  const itemsInstallments = formatInstallments(pendingPayments);

  // Si hay al menos un item de pago y el descuento ciclo completo está activado
  if (requestBenefit && discountPayment) {
    // feeItems === Cuotas
    const feeItems = items.filter((item: any) => item.paymentType.id === '02');
    const notFeeItems: IPaymentItem[] = items.filter(
      (item: any) => item.paymentType.id !== '02'
    );

    if (feeItems?.length === 0) return notFeeItems;

    const totals = feeItems?.reduce(
      (
        acc: {
          amount: number;
          discountCicloCompleto: number;
          amountToPay: number;
          discountBene: number;
        },
        item: any
      ) => {
        acc.amount += parseFloat(item.amount);
        acc.discountCicloCompleto += parseFloat(item.discountCicloCompleto);
        acc.amountToPay += parseFloat(item.amountToPay);
        acc.discountBene += parseFloat(item.discountBene);
        return acc;
      },
      { amount: 0, discountCicloCompleto: 0, amountToPay: 0, discountBene: 0 }
    );

    const totalAmount = formatAmount(totals?.amount);
    const totalAmountToPay = totals?.amountToPay?.toFixed(2);
    const totalAmountToPayWithBenefits = formatAmount(totals?.amountToPay);
    const totalDiscountCicloCompleto = totals?.discountCicloCompleto;
    const totalDiscountBene = Number(formatAmount(totals?.discountBene));

    const newBenefitActivatedItem = {
      name: 'Ciclo completo',
      isFullCycleDiscount: true,
      periodDesc: feeItems[0]?.periodDesc,
      itemNumber: 'total',
      amount: totalAmount,
      amountWithBenefits: totalAmountToPayWithBenefits,
      amountToPay: totalAmountToPay,
      status: feeItems[0]?.status,
      hasDiscountProntoPago: false,
      paymentType: {
        id: '10%',
        desc: 'Pensiones'
      },
      dueDate: feeItems[0]?.dueDate,
      recharge: '0.00',
      discountProntoPago: feeItems[0]?.discountProntoPago,
      discountCicloCompleto: totalDiscountCicloCompleto,
      discountBene: totalDiscountBene,
      dateCreate: feeItems[0]?.dateCreated,
      itemsIds: itemsInstallments.map((item: any) => item.itemNumber)
    };

    // Añado itemCard de ciclo completo
    notFeeItems.push(newBenefitActivatedItem);

    return notFeeItems.sort((a, b) => (a.dueDate ?? 0) - (b.dueDate ?? 0));
  }

  return items;
};

export const generateIziConfig = async (
  amount: number,
  transactionId: string,
  orderId: string,
  token: string,
  merchantCode: string,
  phone: string,
  campus: string,
  studentCode: string,
  callbackResponse: (response: any) => void
) => {
  if ((window as any).Izipay) {
    try {
      const iziConfig = {
        config: {
          transactionId,
          action: 'pay',
          merchantCode,
          appearance: {
            logo: `https://www.utp.edu.pe/sites/default/files/2024-05/logo-utp-200px.png`,
            customize: {
              elements: [
                {
                  paymentMethod: 'CARD',
                  order: 1,
                  fields: [
                    {
                      name: 'typeDocument',
                      visible: true
                    },
                    {
                      name: 'documentNumber',
                      visible: true
                    }
                  ]
                }
              ]
            },
            customTheme: {
              colors: {
                primary: {
                  background: '#5B36F2',
                  color: '#FFFFFF'
                }
              }
            }
          },
          order: {
            orderNumber: orderId,
            currency: 'PEN',
            amount,
            processType: 'AT',
            merchantBuyerId: studentCode,
            dateTimeTransaction: (Math.floor(Date.now()) * 1000).toString(),
            payMethod: 'CARD'
          },
          billing: {
            firstName: '',
            lastName: '',
            phoneNumber: phone,
            street: campus,
            city: campus,
            state: 'Lima',
            country: 'PE',
            postalCode: '15001',
            documentType: 'DNI',
            document: ''
          },
          render: {
            typeForm: 'pop-up'
          },
          urlIPN: process.env.REACT_APP_URL_IPN_IZIPAY
        }
      };
      const checkoutInstance = new (window as any).Izipay({
        config: iziConfig.config
      });
      await checkoutInstance.LoadForm({
        authorization: token,
        keyRSA: 'RSA',
        callbackResponse
      });
    } catch ({ Errors, message, date }: any) {
      throw new Error(`${Errors}, ${message}, ${date}`);
    }
  }
};
